import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Maximize2, Car, Clock, MapPin, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

// Traffic camera info - links to mobilitat.ad
const TRAFFIC_CAMERAS = [
  {
    id: 'frontera',
    name: 'Frontera Hispano-Andorrana',
    location: 'CG1 - PK 11+195',
    distance: '8.7 km'
  },
  {
    id: 'rotonda-olivella',
    name: 'Rotonda Olivella',
    location: 'CG1',
    distance: '12 km'
  },
  {
    id: 'portalet',
    name: 'Portalet',
    location: 'Andorra',
    distance: '15 km'
  }
];

const REFRESH_INTERVAL = 5 * 60 * 1000; // 5 minuts

export const TrafficCard = () => {
  const { language } = useLanguage();
  const [currentCamera, setCurrentCamera] = useState(0);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [imageKey, setImageKey] = useState(Date.now());
  const [isLoading, setIsLoading] = useState(false);

  const texts = {
    ca: {
      title: 'Estat del Trànsit',
      subtitle: 'Càmeres de trànsit en temps real',
      updated: 'Actualitzat fa',
      minutes: 'min',
      viewAll: 'Veure totes les càmeres',
      distance: 'Distància',
      liveView: 'Vista en directe'
    },
    es: {
      title: 'Estado del Tráfico',
      subtitle: 'Cámaras de tráfico en tiempo real',
      updated: 'Actualizado hace',
      minutes: 'min',
      viewAll: 'Ver todas las cámaras',
      distance: 'Distancia',
      liveView: 'Vista en directo'
    },
    fr: {
      title: 'État du Trafic',
      subtitle: 'Caméras de circulation en temps réel',
      updated: 'Mis à jour il y a',
      minutes: 'min',
      viewAll: 'Voir toutes les caméras',
      distance: 'Distance',
      liveView: 'Vue en direct'
    },
    en: {
      title: 'Traffic Status',
      subtitle: 'Real-time traffic cameras',
      updated: 'Updated',
      minutes: 'min ago',
      viewAll: 'View all cameras',
      distance: 'Distance',
      liveView: 'Live view'
    }
  };

  const t = texts[language] || texts.ca;
  const camera = TRAFFIC_CAMERAS[currentCamera];

  // Refresh image
  const refreshImage = useCallback(() => {
    setIsLoading(true);
    setImageKey(Date.now());
    setLastUpdate(new Date());
    setTimeout(() => setIsLoading(false), 1000);
  }, []);

  // Calculate minutes since last update
  const getMinutesSinceUpdate = () => {
    const now = new Date();
    const diff = Math.floor((now - lastUpdate) / 60000);
    return diff < 1 ? '< 1' : diff;
  };

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(refreshImage, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [refreshImage]);

  // Update minutes display
  const [minutesAgo, setMinutesAgo] = useState(getMinutesSinceUpdate());
  useEffect(() => {
    const timer = setInterval(() => {
      setMinutesAgo(getMinutesSinceUpdate());
    }, 30000);
    return () => clearInterval(timer);
  }, [lastUpdate]);

  const nextCamera = () => {
    setCurrentCamera((prev) => (prev + 1) % TRAFFIC_CAMERAS.length);
    refreshImage();
  };

  const prevCamera = () => {
    setCurrentCamera((prev) => (prev - 1 + TRAFFIC_CAMERAS.length) % TRAFFIC_CAMERAS.length);
    refreshImage();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.1 }}
      className="bg-white border border-[#E5E5E5] overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
      data-testid="traffic-card"
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
        <div>
          <h3 className="font-display text-xl text-[#1A1A1A] font-medium">
            {t.title}
          </h3>
          <p className="text-sm text-[#666666] mt-0.5">{t.subtitle}</p>
        </div>
        <button
          onClick={refreshImage}
          className={`p-2 text-[#666666] hover:text-[#1A4D2E] hover:bg-[#F0F2EF] rounded-full transition-colors duration-200 ${isLoading ? 'animate-spin' : ''}`}
          aria-label="Refresh"
          data-testid="traffic-refresh-btn"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Camera Preview - Link to mobilitat.ad */}
      <div className="relative aspect-video bg-gradient-to-br from-[#1A4D2E] to-[#2d6b47]">
        <a 
          href="https://www.mobilitat.ad/totes-les-cameres"
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full h-full cursor-pointer group"
        >
          {/* Background pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
          
          {/* Content */}
          <div className="relative z-10 flex flex-col items-center justify-center h-full text-white p-6">
            <Car className="w-12 h-12 mb-4 opacity-80" />
            <h4 className="text-lg font-medium mb-2">{camera.name}</h4>
            <p className="text-sm opacity-80 mb-4">{camera.location} • {camera.distance}</p>
            <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full group-hover:bg-white/30 transition-colors">
              <ExternalLink className="w-4 h-4" />
              <span className="text-sm font-medium">{t.viewAll}</span>
            </div>
          </div>

          {/* Live indicator */}
          <div className="absolute top-4 left-4 flex items-center gap-2 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-xs text-white font-medium uppercase tracking-wider">Live</span>
          </div>
        </a>

        {/* Navigation arrows */}
        <button
          onClick={(e) => { e.preventDefault(); prevCamera(); }}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-colors z-20"
          aria-label="Previous camera"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={(e) => { e.preventDefault(); nextCamera(); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center text-white transition-colors z-20"
          aria-label="Next camera"
        >
          <ChevronRight className="w-5 h-5" />
        </button>

        {/* Camera counter */}
        <div className="absolute bottom-4 right-4 bg-black/40 backdrop-blur-sm px-3 py-1.5 rounded-full">
          <span className="text-xs text-white font-medium">{currentCamera + 1} / {TRAFFIC_CAMERAS.length}</span>
        </div>
      </div>

      {/* Camera Info Bar */}
      <div className="px-6 py-4 bg-[#F9F9F7] flex flex-wrap items-center justify-between gap-4">
        {/* Location */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Car className="w-5 h-5 text-[#1A4D2E]" />
            <span className="text-sm font-medium text-[#1A1A1A]">{camera.name}</span>
          </div>
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#666666]" />
            <span className="text-sm text-[#666666]">{camera.distance}</span>
          </div>
        </div>
        {/* Last update */}
        <div className="flex items-center gap-2 text-sm text-[#666666]">
          <Clock className="w-4 h-4" />
          <span>{t.updated} {minutesAgo} {t.minutes}</span>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 border-t border-[#E5E5E5] flex flex-wrap gap-3">
        <a
          href="https://www.mobilitat.ad/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 min-w-[140px] bg-[#1A4D2E] text-white px-4 py-3 text-xs font-semibold tracking-[0.1em] uppercase hover:bg-[#153d24] transition-colors duration-200 flex items-center justify-center gap-2"
          data-testid="traffic-view-btn"
        >
          <Maximize2 className="w-4 h-4" />
          {t.viewAll}
        </a>
        <a
          href="https://www.google.com/maps/dir//UGOLF+ARAVELL+Andorra,+Carretera+de+Tor,+s/n,+25598+Aravell,+Lleida/@42.4023,-1.4603,12z"
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 min-w-[140px] bg-transparent border-2 border-[#1A4D2E] text-[#1A4D2E] px-4 py-3 text-xs font-semibold tracking-[0.1em] uppercase hover:bg-[#1A4D2E] hover:text-white transition-colors duration-200 flex items-center justify-center gap-2"
          data-testid="traffic-directions-btn"
        >
          <MapPin className="w-4 h-4" />
          {language === 'ca' ? 'Com arribar' : language === 'es' ? 'Cómo llegar' : language === 'fr' ? 'Itinéraire' : 'Directions'}
        </a>
      </div>

      {/* Camera selector buttons */}
      <div className="px-6 py-3 border-t border-[#E5E5E5] flex gap-2 overflow-x-auto">
        {TRAFFIC_CAMERAS.map((cam, index) => (
          <button
            key={cam.id}
            onClick={() => {
              setCurrentCamera(index);
            }}
            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              currentCamera === index 
                ? 'bg-[#1A4D2E] text-white' 
                : 'bg-[#F0F2EF] text-[#666666] hover:bg-[#E5E5E5]'
            }`}
          >
            {cam.name.split(' ')[0]}
          </button>
        ))}
      </div>
    </motion.div>
  );
};

export default TrafficCard;
